/**
 * The API client.
 *
 * Three things it takes seriously.
 *
 * 1. AMOUNTS ARE STRINGS. Always. They leave the backend as exact decimal strings and they stay
 *    strings all the way to the screen. The moment a balance becomes a JavaScript number it becomes
 *    an IEEE-754 double, and 0.1 is not exactly representable in one. This is the participant's
 *    money.
 *
 * 2. A BLOCKED MOVEMENT IS NOT AN ERROR. The backend returns 200 with `outcome: 'blocked'` and a
 *    disclosable reason, because "your daily limit is reached" and "the server fell over" are
 *    different things and the participant needs to be able to tell them apart and act.
 *
 * 3. 401 AND 403 MEAN DIFFERENT THINGS, and the app must act differently on each.
 *
 *    401  your session is gone or was never there.   -> drop the token, show sign-in.
 *    403  you are signed in, and this is not yours.  -> show the refusal. Do NOT sign out.
 *
 *    Collapsing them is the classic bug: a user who merely lacks access gets bounced into a sign-in
 *    loop that can never succeed, and a user whose session expired gets told they are not permitted.
 */

import { clearToken, getToken } from './session';
import { MOCKS_ON, MockRefusal, mockCall } from './mocks';

const BASE = import.meta.env['VITE_API_URL'] ?? 'http://localhost:4000/api/v1';

export interface Participant {
  id: string;
  type: 'individual' | 'business';
  tier: 'T0' | 'T1' | 'T2' | 'T3';
  status: string;
  whitelisted: boolean;
}

export interface Balance {
  asset: string;
  /** An exact decimal string. Never a number. */
  available: string;
  locked: string;
  total: string;
}

export interface Wallet {
  participantId: string;
  frozen: boolean;
  balances: Balance[];
}

export interface Lock {
  asset: string;
  amount: string;
  reason: string;
  /** Plain language. The same words the operator and the regulator see. */
  reasonLabel: string;
  causedBy: { kind: string; id: string };
}

/**
 * The rails a withdrawal can leave on. These two strings are the backend's `Literal`, not labels:
 * `mobile_money` is what M-Pesa is called in `txn.ramp_instruction`, and renaming it here would
 * silently produce a 422 on every payout.
 */
export type WithdrawalRail = 'bank_transfer' | 'mobile_money';

/** A saved place a participant can be paid out to. */
export interface PayoutDestination {
  id: string;
  rail: WithdrawalRail;
  label: string;
  /** Masked for display. The unmasked value is what goes to the rail as `destination_ref`. */
  masked: string;
  ref: string;
  /** Unverified destinations are shown but cannot be selected. Connected accounts states the rule:
   *  "A withdrawal only goes to a verified destination." */
  verified: boolean;
}

/**
 * What an accepted withdrawal returns. SNAKE_CASE, because it is the backend's dict verbatim.
 *
 * `state: 'instructed'` is NOT 'paid'. The money is locked in escrow and has left the spendable
 * balance, but it has not left the platform: it does that when the rail confirms, and it comes back
 * if the rail refuses. The UI must not say "sent".
 */
export interface WithdrawalReceipt {
  instruction_id: string;
  state: string;
  amount: string;
  correlation_id: string;
  note: string;
}

/** A deposit or withdrawal in this participant's ramp history. Snake_case, as sent. */
export interface RampInstruction {
  instruction_id: string;
  direction: 'deposit' | 'withdrawal';
  rail: WithdrawalRail;
  /** Already formatted by the backend's Money.format(). Do not re-format it as a number. */
  amount: string;
  asset: string;
  state: string;
  provider_reference: string | null;
  failure_reason: string | null;
  created_at: string;
  confirmed_at: string | null;
}

/**
 * What the pipeline returns.
 *
 * SNAKE_CASE, because that is what the backend actually sends: the endpoints return `asdict(result)`
 * over the pipeline's frozen dataclasses, with no serialisation layer in between and a gateway that
 * is a pure proxy.
 *
 * This type used to be camelCase, which meant `result.correlationId` was `undefined` against the
 * real backend and correct only under UI mocks: the "see what happened" link on a completed payment
 * pointed at `/activity/undefined`. It was invisible because mock mode agreed with the type rather
 * than with the server. `mocks.ts` had already recorded the mismatch as something "to reconcile when
 * the backend is wired", and wiring the instant swap is when.
 */
export type MovementResult =
  | { outcome: 'settled'; transaction_id: string; escrow_id: string; correlation_id: string }
  | {
      outcome: 'blocked';
      stage: string;
      code: string;
      /** The only reason we may show the participant. The internal one goes to the risk officer. */
      disclosable_reason: string;
      correlation_id: string;
    }
  | { outcome: 'rolled_back'; stage: string; reason: string; correlation_id: string };

/** The structured error shape from the API conventions. */
export interface ApiError {
  code: string;
  category: 'policy' | 'transient' | 'validation' | 'internal';
  message: string;
  /**
   * A policy rejection is NEVER retryable. Retrying a cap breach is an attempt to circumvent the
   * cap, so the UI must not offer a retry button on one.
   */
  retryable: boolean;
  /**
   * Structured detail the backend deems safe to disclose. Present on errors that have a next step
   * worth branching on, rather than only a sentence to display.
   *
   * An instant swap refusal carries `refusal` (which of the eight) and `standard_path_available`.
   * FR-U-LQD-002 requires the screen to say WHICH refusal and offer the ordinary route, and neither
   * is derivable from the message text without parsing prose.
   */
  context?: Record<string, unknown>;
}

export class LipaApiError extends Error {
  constructor(readonly detail: ApiError) {
    super(detail.message);
  }
}

/** Your session has ended. The app should drop its token and show the sign-in screen. */
export class NotSignedInError extends LipaApiError {}

/** You are signed in, and this is not yours. Signing in again will not help. */
export class ForbiddenError extends LipaApiError {}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  // UI-ONLY MODE. With the backend switched off, route to fixtures instead of the network. A no-op
  // unless NEXT_PUBLIC_UI_MOCKS=1, and it changes nothing about the real path. See lib/mocks.ts.
  if (MOCKS_ON) {
    try {
      return await mockCall<T>(path, init);
    } catch (err) {
      // A fixture that refuses becomes the SAME error a refusing gateway produces. Otherwise mock
      // mode can only succeed, and every refusal path in the UI stays unexercised until it meets a
      // real backend for the first time.
      if (err instanceof MockRefusal) throw new LipaApiError(err.detail);
      throw err;
    }
  }

  const token = getToken();

  // A FormData body must NOT carry a Content-Type we chose. The browser generates one that includes
  // the multipart boundary, and overriding it with `application/json` produces a request the server
  // cannot parse — with an error that blames the file rather than the header.
  const isMultipart = init?.body instanceof FormData;

  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      ...(isMultipart ? {} : { 'Content-Type': 'application/json' }),
      // The credential. Sent on every call, and it is the ONLY thing that says who we are: the
      // backend takes the principal from this and never from a path or a body, which is why a
      // signed-in Mallory cannot read Alice's wallet by editing a URL.
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
    cache: 'no-store',
  });

  const body = await res.json().catch(() => null);

  if (res.ok) return body as T;

  const detail = (body as ApiError) ?? {
    code: 'INTERNAL_ERROR',
    category: 'internal' as const,
    message: 'Something went wrong. Nothing has moved.',
    retryable: true,
  };

  if (res.status === 401) {
    // The token is dead. Holding on to it would mean every subsequent call fails the same way while
    // the UI keeps pretending the user is signed in.
    clearToken();
    throw new NotSignedInError(detail);
  }

  if (res.status === 403) {
    // NOT a sign-out. The session is perfectly valid; the request was not the caller's to make.
    throw new ForbiddenError(detail);
  }

  throw new LipaApiError(detail);
}

/** What the backend returns when a session begins. The token is shown ONCE and never again. */
export interface Session {
  token: string;
  participant_id: string;
  tier: 'T0' | 'T1' | 'T2' | 'T3';
  status: string;
  session_id: string;
}

/** Who am I, really. Fetched on every boot; never cached, never inferred from the token. */
export interface Me {
  participant_id: string;
  tier: 'T0' | 'T1' | 'T2' | 'T3';
  status: string;
  tiers?: { tier: string; label: string; unlocks: string[] }[];
}

export type DocType =
  | 'national_id'
  /** The reverse of a national ID. Its own type, because the two sides are separate evidence. */
  | 'national_id_back'
  | 'passport'
  | 'proof_of_address'
  | 'selfie'
  | 'business_registration';

/** A mandatory disclosure and whether the user has acknowledged the current version (FR-U-DSP-004). */
export interface Disclosure {
  type: string;
  version: string;
  title: string;
  body: string;
  acknowledged: boolean;
  acknowledged_version: string | null;
  acknowledged_at: string | null;
}

export interface Tier1Request {
  /** As it appears ON THE DOCUMENT. This is the string that gets screened against sanctions lists. */
  full_name: string;
  /** ISO date. */
  date_of_birth: string;
  /** ISO 3166-1 alpha-2. */
  nationality: string;
  id_document_type: 'national_id' | 'passport';
  id_document_number: string;
  /** Ids from uploadDocument. NOT hashes: a hash the client picks is a fact about nothing. */
  document_id: string;
  selfie_id: string;
  /**
   * The REVERSE of a national ID. Required with `national_id`, refused with `passport`.
   *
   * Optional in the type because the two document kinds genuinely differ: a passport's biographical
   * page is the whole document, and a national ID has a back carrying the district and place of
   * issue. The backend validates the pairing, so sending the wrong combination is a 422 rather than
   * a silently half-captured identity.
   */
  document_back_id?: string;
}

export interface Tier2Request {
  address: { line1: string; line2?: string; city: string; county?: string; country?: string };
  address_proof_id: string;
  source_of_funds: {
    category:
      'salary' | 'business_income' | 'investment' | 'inheritance' | 'gift' | 'savings' | 'other';
    description: string;
    /** MINOR UNITS, as a string. Never a JSON number — it is compared against exact ledger amounts. */
    expected_monthly_volume: string;
    asset?: 'KES';
  };
}

/**
 * What the participant has left, BEFORE they compose a payment.
 *
 * `available: false` means the platform cannot tell — a cap is not configured — and because caps FAIL
 * CLOSED that means every movement will be refused. It does NOT mean "unlimited", and the UI must
 * never render it as a generous green bar.
 */
export type Limits =
  | { available: false; asset: string; phase: string; missing: string[] }
  | {
      available: true;
      asset: string;
      phase: string;
      /** All amounts are exact integers of MINOR UNITS, as strings. Never JSON numbers. */
      per_transaction: { limit: string };
      daily: { limit: string; used: string; remaining: string; resets_at: string };
      cumulative: { limit: string; used: string; remaining: string; resets_at: null };
    };

/**
 * TOKENIZATION.
 *
 * The one thing to carry in your head, because the whole design turns on it (ADR 0005):
 *
 *   A TOKEN REPRESENTS AN INTEREST IN A LEGAL ENTITY THAT HOLDS THE ASSET.
 *   IT DOES NOT REPRESENT THE ASSET.
 *
 * Land is transferred by the land register, and a token is not the land register. So for real estate
 * and land the platform requires a legal entity — a company or an SPV — that HOLDS the property, and
 * the token is an interest in that entity. RealT does exactly this: "RealTokens are effectively
 * digital limited liability company membership interests", and the deed lists the Series as the
 * recorded owner.
 *
 * A vehicle does not need one: that registration regime does recognise transfer of the thing itself.
 */
export type AssetClass = 'real_estate' | 'land' | 'vehicle' | 'inventory' | 'receivable';

/** The asset classes that legally CANNOT be represented by a bare token. See ADR 0005. */
export const NEEDS_LEGAL_WRAPPER: AssetClass[] = ['real_estate', 'land'];

export interface MyAsset {
  asset_id: string;
  class: AssetClass;
  ownership_reference: string;
  verification_status: string;
  submitted_at: string;
  token_id: string | null;
}

/**
 * FR-U-TOK-002. A CHECKLIST, not a status string.
 *
 * "Under review" tells an owner nothing about what is actually missing. Each gate below is a way the
 * platform could otherwise issue a token that is worthless, or fraudulent, or both — so the owner is
 * shown which one they are waiting on.
 */
export interface AssetStatus {
  asset_id: string;
  class: AssetClass;
  ownership_reference: string;
  verification_status: string;
  timeline: {
    /** null for a vehicle: that register recognises transfer of the thing. */
    legal_entity_holds_it: boolean | null;
    ownership_proof_provided: boolean;
    land_register_searched: boolean | null;
    /** Did the register AGREE with what the submitter claimed? The dual-ledger reconciliation. */
    land_register_agreed: boolean | null;
    valuation_confirmed: boolean;
    token_issued: boolean;
  };
  ready_to_mint: boolean;
  /** What is stopping it, in plain words. */
  blocking: string[];
  token_id: string | null;
}

export interface TokenDetail {
  token_id: string;
  /** security | utility | asset_backed. FR-X-TOK-008. */
  classification: string;
  /** issued | locked | transferred | redeemed | burned. FR-X-TOK-006. */
  state: string;
  minted_at: string;

  asset: { id: string; class: AssetClass; ownership_reference: string };

  /** NULL only for an asset class whose register recognises transfer of the thing (a vehicle). */
  legal_entity: {
    id: string;
    legal_name: string;
    kind: string;
    jurisdiction: string;
    registration_number: string;
  } | null;

  /**
   * What the holder ACTUALLY owns, in the platform's own words.
   *
   * It does not say "you own this land", because they do not, and a certificate that said so would be
   * telling the holder something untrue. This string is rendered verbatim.
   */
  what_you_own: string;

  /** Exact MINOR UNITS as a string. This is the figure that drives loan-to-value if it is pledged. */
  current_valuation: {
    amount: string;
    asset_code: string;
    method: string;
    effective_from: string;
  } | null;

  /** THE DUAL-LEDGER RECONCILIATION: what the land register actually said. */
  title_verified_against: {
    registry: string;
    registered_owner: string | null;
    checked_at: string;
  } | null;

  holders: { holder_id: string; fraction_bps: number }[];
}

export interface Holding {
  token_id: string;
  /** Basis points. 10,000 = the whole thing. */
  fraction_bps: number;
  classification: string;
  state: string;
  asset_class: AssetClass;
  ownership_reference: string;
}

/**
 * A notification. UF-08.
 *
 * `mandatory` is the field that matters, and it is why this type is not just a string and a date.
 *
 * FR-X-NOT-002: regulatory and risk notices are mandatory and cannot be silenced. The app has to KNOW
 * which ones those are, or it will eventually grow a "mute all" toggle that quietly disables fraud
 * alerts — and whoever steals an account will use it first.
 *
 * `read` and `acknowledged` are different facts, and collapsing them would be a legal error rather
 * than a UI one: scrolling past a risk disclosure must never count as accepting it.
 */
export interface Notification {
  id: string;
  category: string;
  subject: string;
  content: string;
  /** Cannot be silenced. FR-X-NOT-002. */
  mandatory: boolean;
  /** Seen. */
  read: boolean;
  /** AGREED to. A legal act. Only meaningful on a mandatory notice. */
  acknowledged: boolean;
  at: string;
}

/**
 * A dispute. UF-07.
 *
 * `we_will_answer_by` is the field that matters, and it is set the moment the participant presses
 * send — not when an officer picks the case up. A deadline that starts when we get round to it is a
 * promise that the clock only runs when we are looking.
 */
export type DisputeCategory =
  | 'unauthorized_transaction'
  | 'payment_not_received'
  | 'wrong_amount'
  | 'escrow_not_released'
  | 'account_frozen'
  | 'verification_rejected'
  | 'tokenization_issue'
  | 'other';

/**
 * ASSISTED HELP (FR-U-DSP-006). Contained by ADR 0007.
 *
 * Note what is NOT in this type: nothing about the participant. The assistant cannot see a balance, a
 * transaction, a case, or a tier, because the service behind it connects as a database role with
 * USAGE on one schema and nothing else. If a future shape here starts carrying account data, that is
 * the boundary moving and it needs an FR, not a field.
 */
export interface AssistantCitation {
  /** `slug@version`, so a transcript can be traced back to the exact published text. */
  source_id: string;
  title: string;
  /**
   * The published passage, VERBATIM. Render THIS, not a paraphrase of it: the tokenization
   * certificate established the rule and ADR 0007 rule 6 extends it here. A paraphrase is where the
   * meaning would quietly go.
   */
  quote: string;
}

export interface AssistantAnswer {
  conversation_id: string;
  body: string;
  citations: AssistantCitation[];
  /**
   * True when the assistant declined rather than guessed. NOT an error, and it must not be styled
   * like one: it is the correct outcome for a question our help articles do not cover, and it is the
   * behaviour that keeps a confidently wrong answer from reaching somebody who is worried about
   * their money.
   */
  handed_off: boolean;
}

export interface AssistantAvailability {
  /** False while the provider is the simulator. The UI says so rather than pretending. */
  enabled: boolean;
  consented: boolean;
  disclosure: { type: string; version: string; title: string; body: string };
}

export interface Dispute {
  case_id: string;
  category: DisputeCategory;
  /** Set from the CATEGORY, never by the participant. They would set it to urgent, always. */
  priority: 'low' | 'normal' | 'high' | 'urgent';
  status: 'open' | 'investigating' | 'awaiting_participant' | 'resolved' | 'withdrawn';
  subject: string;
  detail?: string;
  correlation_id?: string | null;
  opened_at: string;
  sla_due_at: string;
  resolved_at: string | null;
  /** What we decided, in words written for THEM. Not the officer's internal notes. */
  resolution: string | null;
  compensation?: {
    amount: string;
    asset: string;
    status: 'assessed' | 'approved' | 'paid' | 'rejected';
    paid_at: string | null;
  } | null;
}

export interface ActiveSession {
  session_id: string;
  device_name: string;
  platform: string | null;
  ip_address: string | null;
  started_at: string;
  last_active_at: string;
}

// ---------------------------------------------------------------------------
// Marketplace + Investor Portal (super-app). A single verified identity can invest in tokenized
// real-world assets and hold a portfolio, with no separate login. Money fields are formatted decimal
// strings; percentages are plain numbers, for display only.
// ---------------------------------------------------------------------------

export type RiskBand = 'low' | 'medium' | 'high';

export interface Listing {
  id: string;
  title: string;
  category: string;
  location: string;
  /** A palette tone key for the card header gradient. Still the fallback, and still what shows
   *  wherever `image` is absent or fails to load. */
  tone: string;
  /**
   * Card artwork, served from this app's own public/demo/assets.
   *
   * Optional on purpose: a listing without one falls back to the gradient rather than to a broken
   * frame. Same-origin illustration in the app's token palette, never photography -- a marketplace
   * of tokenised Kenyan assets rendered as bare gradients reads as a spreadsheet, and a stock
   * photograph of a building nobody owns would be a different kind of untruth.
   */
  image?: string;
  yieldPct: number;
  fundedPct: number;
  target: string;
  raised: string;
  pricePerToken: string;
  minInvest: string;
  risk: RiskBand;
  /** The Special Purpose Trust that legally holds the asset (the Trust Entity layer). */
  trust: string;
  investors: number;
}

export interface Marketplace {
  categories: string[];
  listings: Listing[];
}

export interface Portfolio {
  value: string;
  invested: string;
  returnsPct: number;
  incomeToDate: string;
  projectedAnnual: string;
  riskScore: string;
  allocation: { label: string; pct: number; tone: string }[];
  holdings: {
    id: string;
    title: string;
    category: string;
    units: number;
    value: string;
    returnPct: number;
    income: string;
  }[];
  income: { source: string; kind: string; amount: string; at: string }[];
}

export interface Earnings {
  totalEarned: string;
  thisMonth: string;
  projectedAnnual: string;
  /** Available to withdraw now. */
  available: string;
  byType: { type: string; amount: string; pct: number; tone: string }[];
  distributions: { source: string; kind: string; amount: string; at: string }[];
  upcoming: { source: string; kind: string; estAmount: string; dueDate: string }[];
}

export interface MarketAsset {
  id: string;
  title: string;
  category: string;
  tone: string;
  lastPrice: string;
  /** 24h price move, in percent. */
  change24h: number;
  bid: string;
  ask: string;
  volume24h: string;
  /** How many units the current holder owns (0 = nothing to sell). */
  yourUnits: number;
}

// ---------------------------------------------------------------------------
// Merchant + Business portals (Phase 5).
// ---------------------------------------------------------------------------

/**
 * One offer on the marketplace book (FR-U-TXN-001).
 *
 * The willing-buyer-willing-seller flow: you trade with another participant at their price, and
 * escrow holds the seller's asset until somebody confirms. Distinct from the instant swap below,
 * where the counterparty is VASP and it settles immediately.
 */
export interface P2POffer {
  id: string;
  side: 'buy' | 'sell';
  asset: string;
  quote_asset: string;
  price: string;
  remaining: string;
  min: string;
  max: string;
  methods: string[];
  terms: string | null;
  /**
   * WHICH release path a trade on this offer will take, decided by the accepted methods.
   *
   * FR-U-TXN-012 requires both sides to know this BEFORE either commits: a seller needs to know
   * whether they will be asked to confirm receipt, and a buyer needs to know whether their payment
   * method earns the fast path.
   */
  release_path: 'rail_settled' | 'counterparty_confirmed';
  /** Never render this without also rendering WHICH methods earn it. */
  settles_automatically: boolean;
}

export interface P2P {
  offers: P2POffer[];
  /**
   * False today, and honestly so: no payment method is one the platform settles, because every
   * settlement adapter is a simulator. Every trade therefore takes the manual path.
   */
  automatic_settlement_available: boolean;
}

/** What starting a trade returns. A blocked outcome is a result, not an error. */
/**
 * A payment method, and whether it earns the automatic release path.
 *
 * `settles_automatically` is served, never assumed: it flips under maker-checker when a PSP contract
 * lands (migration 0043). A client holding its own copy would keep advertising the old answer, and
 * the direction that fails in is the dangerous one.
 */
export interface P2PPaymentMethod {
  code: string;
  label: string;
  settles_automatically: boolean;
}

export interface P2PPaymentMethods {
  methods: P2PPaymentMethod[];
  automatic_settlement_available: boolean;
}

/**
 * A USDT TILL: a payment handle a participant hands out, the way a business hands out a Lipa na
 * M-Pesa till number.
 *
 * Snake_case, because it is the wire shape.
 *
 * WHAT IS AND IS NOT HERE MATTERS. A till carries the till number, the asset, the network and a
 * RECEIVING ADDRESS, and nothing else about the wallet behind it. No key material, no custody
 * reference, no internal wallet id: a till is meant to be posted in a shop window, and anything on
 * this object is something a stranger will end up holding. The mapping from till to participant lives
 * in the service, and the only way to travel it is `resolveTill`, which answers with a display name
 * and nothing more.
 *
 * `address_is_simulated` is served rather than assumed. Custody is an MPC SIMULATOR today (CLAUDE.md
 * gap 10), so the address a till shows is not one any chain will settle to, and a screen that let a
 * participant believe otherwise would be inviting them to lose real money. The day custody is real
 * this flips server side and the copy follows it, with no deploy here.
 */
export interface Till {
  id: string;
  /**
   * `LP-USDT-TRC20-4K7Q2M`. The format is load-bearing, not decoration.
   *
   * A till is read off a counter, dictated down a phone and typed by a stranger, and the two things
   * that decide whether their money arrives are WHICH ASSET and WHICH NETWORK. A bare `LP-4K7Q2M`
   * carries neither, so a payer holding it has to be told the rest out of band -- and USDT sent on
   * the wrong chain to a right-looking address is the single most common way people lose stablecoin.
   * Putting both in the handle makes the mapping legible before anybody pays, and makes a mismatch
   * visible rather than silent.
   */
  till_number: string;
  participant_id: string;
  asset: string;
  /** The chain, in the words a person uses: "Tron", "BNB Smart Chain". */
  network: string;
  /** The token standard as it appears in the till and on every exchange: TRC20, ERC20, BEP20. */
  network_tag: string;
  /** The receiving address, safe to publish. Never a key, never a custody handle. */
  wallet_address: string;
  status: 'active' | 'inactive';
  created_at: string;
  address_is_simulated: boolean;
}

/**
 * One chain a till can be issued on.
 *
 * SERVED, never hardcoded in a screen. The platform has no chain concept at all today -- custody is
 * an MPC simulator (CLAUDE.md gap 10) and nothing in `backend/src/lipa` knows what a network is -- so
 * a component holding its own list would be a UI asserting an operational fact nobody has
 * established. When custody is real this list is whatever the custodian actually supports, and the
 * screens follow it without a deploy.
 */
export interface TillNetwork {
  /** Stable identifier used in requests and inside the till number. */
  tag: string;
  label: string;
  /** False while custody cannot settle on this chain. The screen must say so. */
  live: boolean;
  /** Anything a payer must know before choosing this chain, in plain words. */
  note?: string;
}

export interface TillAssetOption {
  asset: string;
  name: string;
  networks: TillNetwork[];
}

export interface TillOptions {
  assets: TillAssetOption[];
  /** True while every network is a simulation. One field, so no screen can forget to disclose it. */
  custody_is_simulated: boolean;
}

/**
 * What a till resolves to for somebody about to pay it.
 *
 * The PAYER learns a display name and the asset they must send. They do not learn the recipient's
 * participant id, their phone number or anything else the till was not meant to disclose: resolving
 * a payment handle must not be a directory lookup for the whole account.
 */
export interface TillResolution {
  till_number: string;
  asset: string;
  network: string;
  network_tag: string;
  recipient_name: string;
  /** Where the value actually goes. The participant id the send pipeline needs. */
  recipient_id: string;
  wallet_address: string;
  status: 'active' | 'inactive';
}

/** One trade, as one of its two parties sees it. Role and path come from the server, never the URL. */
export interface P2PTrade {
  id: string;
  state: TradeStep['state'];
  asset: string;
  amount: string;
  release_path: 'rail_settled' | 'counterparty_confirmed';
  settles_automatically: boolean;
  viewer_role: 'buyer' | 'seller';
  method: string;
  step_expires_at: string | null;
  escrow_held: boolean;
}

/**
 * What each settlement mode would cost, priced BEFORE the merchant picks one (FR-U-LQD-003).
 *
 * `instant` is null only when the asset has no instant pricing at all, which is a different
 * statement from "costs zero". When instant exists but cannot be used right now, it is still priced
 * and `instant_refusal` says why: hiding the option would tell the merchant nothing, which is the
 * silent failure FR-U-LQD-002 exists to prevent.
 */
export interface SettlementQuote {
  asset: string;
  payment_count: number;
  gross: string;
  standard: { fee: string; net: string; arrives: string };
  instant: { fee: string; fee_bps: number; net: string; arrives: string } | null;
  instant_available: boolean;
  instant_refusal: string | null;
}

export interface SettlementResult {
  settlement_id: string;
  mode: 'standard' | 'instant';
  outcome?: string;
  reason?: string;
  status?: string;
  note?: string;
  gross?: string;
  fee?: string;
  net?: string;
  advance_id?: string;
}

/** An advance the acquiring cycle has not yet repaid. The platform's money, still out. */
export interface FloatAdvance {
  id: string;
  settlement_id: string;
  asset: string;
  gross: string;
  fee: string;
  net: string;
  advanced_at: string;
}

export type TakeOfferResult =
  | {
      outcome: 'started';
      trade_id: string;
      release_path: 'rail_settled' | 'counterparty_confirmed';
      settles_automatically: boolean;
      escrow_id: string;
      correlation_id: string;
    }
  | { outcome: 'blocked'; reason: string; stage: string };

/** A step in the trade room. `released` is the only one that has moved the asset. */
export interface TradeStep {
  trade_id: string;
  state:
    | 'locked_in_escrow'
    | 'awaiting_payment'
    | 'payment_marked'
    | 'awaiting_release'
    | 'released'
    | 'cancelled'
    | 'under_dispute';
  released: boolean;
  reason?: string;
  escrow_held?: boolean;
}

/** One message in a trade's buyer↔seller chat. `mine` is resolved server-side per viewer. */
export interface P2PMessage {
  id: string;
  body: string;
  mine: boolean;
  sender_id: string;
  is_read: boolean;
  at: string;
}

// ---------------------------------------------------------------------------
// Instant swap: the SECOND peer-to-peer flow (FR-U-TXN-010, FRS 1.2 domain LQD).
//
// The offer book above is willing-buyer-willing-seller. This is the other kind: you buy from VASP's
// own prefunded float at the rate published for the day, and it settles immediately with
// nobody to wait for. Two products, not one product at two speeds, and the UI has to make that
// legible or people will pick the wrong one.
// ---------------------------------------------------------------------------

/** One pair on today's published board. Both sides, because you pay one and receive the other. */
export interface BoardRate {
  base: string;
  quote: string;
  you_buy_at: string;
  you_sell_at: string;
  published_at: string;
}

export interface SwapBoard {
  /** null when nothing is published today. NOT a reason to fall back to a live market rate. */
  date: string | null;
  rates: BoardRate[];
  fixed_for_the_day: boolean;
  /**
   * Always true, and it must reach the screen. A board advertised as unconditional and then pulled
   * mid-day is worse than one honest about its condition, because the participant planned around it.
   * FR-U-LQD-004 is both halves or it is a comfortable half-truth.
   */
  may_be_withdrawn: boolean;
}

/**
 * Today's price applied to one amount.
 *
 * The four figures are separate FIELDS, not a total to be broken down in the component. FR-U-LQD-001
 * requires them shown separately, and a shape that only carried `you_receive` would let the spread
 * be folded into the rate, hiding what the platform earns inside the price. The shape is the control.
 */
export interface SwapQuote {
  quote_id: string;
  from: { asset: string; amount: string };
  to: { asset: string; amount: string };
  rate: string;
  spread: string;
  spread_bps: number;
  fee: string;
  you_receive: string;
  rate_basis: 'published_daily';
  board_date: string;
  may_be_withdrawn: boolean;
  expires_at: string;
}

/** Which of the eight refusals the treasury gave. Each has a different next step. */
export type LiquidityRefusal =
  | 'no_board_rate'
  | 'board_withdrawn'
  | 'daily_volume_cap'
  | 'insufficient_float'
  | 'exposure_cap'
  | 'suspended'
  | 'stale_rates'
  | 'quote_expired';

/**
 * Read the refusal off an error, or null if this was not one.
 *
 * Exists so no screen has to match on message text. The disclosure sentence is the backend's to
 * write (it is the same sentence a regulator would read); the BRANCH is the client's, and it must
 * key off the code.
 */
export function refusalOf(error: unknown): LiquidityRefusal | null {
  if (!(error instanceof LipaApiError)) return null;
  if (error.detail.code !== 'INSTANT_SETTLEMENT_UNAVAILABLE') return null;
  const refusal = error.detail.context?.refusal;
  return typeof refusal === 'string' ? (refusal as LiquidityRefusal) : null;
}

/** Whether the ordinary (non-instant) route is still open. Six of the eight leave it open. */
export function standardPathAvailable(error: unknown): boolean {
  if (!(error instanceof LipaApiError)) return false;
  return error.detail.context?.standard_path_available === true;
}

export interface Analytics {
  netWorth: string;
  totalReturn: number;
  yield: number;
  bestPerformer: { name: string; pct: number };
  worstPerformer: { name: string; pct: number };
  performance: number[];
  diversification: { label: string; pct: number; tone: string }[];
  cashflow: { month: string; income: string; invested: string }[];
}

export interface Tax {
  summary: { taxableIncome: string; capitalGains: string; withheld: string };
  years: { year: number; income: string; gains: string; ready: boolean }[];
}

export interface ConnectedAccounts {
  banks: { id: string; name: string; masked: string; primary: boolean }[];
  mpesa: { id: string; number: string; verified: boolean }[];
  wallets: { id: string; network: string; address: string; verified: boolean }[];
}

export interface Lender {
  portfolio: {
    deployed: string;
    outstanding: string;
    earned: string;
    activeLoans: number;
    defaultRate: number;
  };
  applications: {
    id: string;
    borrower: string;
    amount: string;
    collateral: string;
    ltvPct: number;
    riskGrade: string;
    status: string;
  }[];
  products: {
    id: string;
    name: string;
    ratePct: number;
    maxLtvPct: number;
    term: number;
    status: string;
  }[];
  loans: {
    id: string;
    borrower: string;
    principal: string;
    outstanding: string;
    status: string;
    nextDue: string;
  }[];
}

export interface Merchant {
  profile: { name: string; till: string; status: string };
  today: { received: string; count: number; avg: string };
  settlement: { pending: string; nextAt: string; destination: string };
  payments: {
    id: string;
    from: string;
    amount: string;
    method: string;
    at: string;
    status: string;
  }[];
  links: { id: string; title: string; amount: string; uses: number; status: string }[];
}

export interface Business {
  profile: { legal_name: string; reg: string; kyb_status: string; tier: string; wallet: string };
  directors: { name: string; role: string; id_ref: string; screening: string }[];
  beneficialOwners: { name: string; pct: number; screening: string }[];
  signatories: { name: string; role: string; permissions: string[] }[];
}

// ---------------------------------------------------------------------------
// Credit & collateral (Phase 4). Borrow against a tokenized asset; the token is pledged and locked so
// it cannot be sold or pledged twice (the collateral registry invariant).
// ---------------------------------------------------------------------------

export interface Collateral {
  token_id: string;
  title: string;
  asset_class: string;
  valuation: string;
  ltvPct: number;
  maxLoan: string;
  status: string;
}

export interface LoanSummary {
  id: string;
  product: string;
  principal: string;
  outstanding: string;
  ratePct: number;
  termMonths: number;
  monthly: string;
  nextDue: string;
  status: string;
  collateral: string;
  paidPct: number;
}

// ---------------------------------------------------------------------------
// Asset Finance Marketplace (borrower side of the two-sided credit marketplace).
//
// Credit revolves around ASSETS, not loans: a sponsor borrows because they own a verified,
// trust-administered asset. So the model leads with borrowing power and eligible assets, then products,
// then a multi-DCP financing request, then competing offers, then the loan and its collateral.
// ---------------------------------------------------------------------------

export interface BorrowingPower {
  portfolioValue: string;
  pledgedValue: string;
  availableEquity: string;
  maxCapacity: string;
  utilizationPct: number;
  eligibleProducts: number;
  interestedDcps: number;
  activeLoans: number;
  repaymentDue: string;
  nextDue: string;
  openOffers: number;
  avgInterestPct: number;
}

export interface EligibleAsset {
  id: string;
  name: string;
  type: string;
  trustStatus: string;
  valuation: string;
  tokenizedValue: string;
  currentLtvPct: number;
  maxBorrowing: string;
  existingLoan: string;
  availableEquity: string;
  maxLtvPct: number;
  eligibleProducts: string[];
  interestedDcps: number;
}

export interface FinanceProduct {
  id: string;
  name: string;
  category: string;
  provider: string;
  providerRating: number;
  interestPct: number;
  maxLtvPct: number;
  tenureMonths: number;
  minValue: string;
  maxAmount: string;
  repayment: string;
  supportedAssets: string[];
  approvalDays: number;
  docs: string[];
}

export interface FinanceApplication {
  id: string;
  asset: string;
  assetId: string;
  product: string;
  amount: string;
  status: string;
  dcps: string[];
  createdAt: string;
  offers: number;
}

export interface FinanceOffer {
  id: string;
  applicationId: string;
  asset: string;
  provider: string;
  providerRating: number;
  interestPct: number;
  aprPct: number;
  maxLtvPct: number;
  tenureMonths: number;
  amount: string;
  fees: string;
  insurance: string;
  repayment: string;
  earlySettlement: string;
  conditions: string[];
  status: string;
}

export interface FinanceLoan {
  id: string;
  product: string;
  provider: string;
  asset: string;
  assetId: string;
  principal: string;
  outstanding: string;
  ratePct: number;
  termMonths: number;
  monthly: string;
  nextDue: string;
  status: string;
  collateral: string;
  paidPct: number;
  disbursedAt: string;
}

export interface FinanceLoanDetail extends FinanceLoan {
  schedule: { n: number; due: string; amount: string; status: string }[];
  timeline: { key: string; label: string; at: string | null; done: boolean }[];
  documents: string[];
}

export interface CollateralRecord {
  asset: string;
  assetId: string;
  provider: string;
  loanId: string;
  tokensLocked: number;
  ltvPct: number;
  outstanding: string;
  releaseOn: string;
  status: string;
  priority: number;
}

export interface FinanceOverview {
  borrowingPower: BorrowingPower;
  eligibleAssets: EligibleAsset[];
  products: FinanceProduct[];
  applications: FinanceApplication[];
  offers: FinanceOffer[];
  loans: FinanceLoan[];
  collateral: CollateralRecord[];
}

export interface AssetPassport {
  id: string;
  name: string;
  type: string;
  location: string;
  trust: {
    id: string | null;
    name: string | null;
    holdingEntity: string;
    trustNumber: string | null;
  };
  valuationHistory: { value: string; at: string; valuer: string }[];
  supply: { total: number; price: string } | null;
  treasury: Treasury | null;
  listings: number;
  capitalRaised: string;
  financing: { loans: number; outstanding: string; maxBorrowing: string; availableEquity: string };
  collateral: CollateralRecord[];
  insurance: string;
  compliance: string[];
}

// ---------------------------------------------------------------------------
// THE TWO-SIDED CREDIT MARKETPLACE (ARCH 05, FR-U-CRD-001 to 012).
//
// Credit has two doors: BORROW and FINANCE. They are two views of ONE set of records -- the same
// requests, the same collateral, the same loans -- which is why the types below are shared rather
// than mirrored per side. Two parallel type families would be two descriptions of one book, and the
// second one to be updated would be the wrong one.
//
// EVERY MONEY FIELD IS `*_minor`: exact minor units, as a STRING. Never a JSON number, because a JSON
// number is an IEEE-754 double by the time it reaches this file and KES 0.1 is not representable in
// one. Format at the edge with `fromMinor`, never arithmetic on the formatted value.
// ---------------------------------------------------------------------------

/** A credit product, from the backend's configuration. Nothing here is hardcoded in a screen. */
export interface CreditProduct {
  id: string;
  name: string;
  description: string | null;
  lender_id: string;
  lender_name: string | null;
  asset_classes: string[];
  max_ltv_bps: number;
  margin_call_ltv_bps: number;
  liquidation_ltv_bps: number;
  haircut_bps: number;
  cure_period_hours: number;
  rate_bps: number;
  tenor_days: number;
  tenor_options_months: number[];
  repayment_frequency: string;
  grace_period_days: number;
  min_amount_minor: string | null;
  max_amount_minor: string | null;
  amount_asset: string;
  fee_schedule: {
    code: string;
    label: string;
    basis: string;
    bps?: number;
    amount_minor?: string;
    when: string;
  }[];
  required_documents: string[];
  proceeds_applicable: boolean;
  eligibility_notes: string | null;
}

/**
 * One tokenized holding, with the eligibility verdict and its REASONS.
 *
 * `reasons` is populated even when eligible (empty), so a screen can show a green tick and still say
 * what it ticked. Quantities are basis points of the token, matching the ownership registry: there is
 * no "token count" anywhere in the platform and inventing one here would denominate the pledged
 * quantity differently from the owned one.
 */
export interface EligibleCollateral {
  token_id: string;
  asset_id: string;
  asset_name: string;
  asset_class: string;
  owned_bps: number;
  available_bps: number;
  pledged_bps: number;
  valuation_minor: string;
  valuation_asset: string;
  valuation_is_stale: boolean;
  eligible: boolean;
  reasons: string[];
}

export interface CollateralPosition {
  id: string;
  request_id: string;
  loan_id: string | null;
  token_id: string;
  asset_id: string;
  asset_name: string | null;
  asset_class: string | null;
  holder_id: string;
  pledged_bps: number;
  valuation_minor: string;
  valuation_asset: string;
  haircut_bps: number;
  eligible_minor: string;
  lien_priority: number;
  state:
    | 'pledged'
    | 'encumbered'
    | 'under_enforcement'
    | 'realization_pending'
    | 'realized'
    | 'released';
  pledged_at: string;
  verified_at: string | null;
  released_at: string | null;
  loan_state?: string | null;
  financer_name?: string | null;
  request_reference?: string | null;
}

export type CreditRequestState =
  | 'draft'
  | 'submitted'
  | 'under_review'
  | 'collateral_verification'
  | 'trust_review'
  | 'open_for_financing'
  | 'financing_interest_received'
  | 'financing_selected'
  | 'borrower_acceptance_pending'
  | 'dcp_approval_pending'
  | 'approved'
  | 'cancelled'
  | 'rejected'
  | 'expired';

export interface CreditRequest {
  id: string;
  reference: string;
  borrower_id: string;
  product_id: string;
  product_name: string | null;
  amount_minor: string;
  asset: string;
  tenor_months: number;
  repayment_frequency: string;
  purpose: string;
  state: CreditRequestState;
  financing_deadline: string | null;
  selected_offer_id: string | null;
  rejected_reason: string | null;
  created_at: string;
  submitted_at: string | null;
  accepted_at: string | null;
  eligible_collateral_minor: string | null;
  max_financing_minor: string | null;
  live_offers: number;
}

export interface CreditRequestDetail extends CreditRequest {
  collateral: CollateralPosition[];
  offers: FinancingOffer[];
  timeline: {
    from_state: string | null;
    to_state: string;
    actor_kind: string;
    actor_role: string | null;
    reason: string | null;
    occurred_at: string;
  }[];
}

/**
 * A financer's terms against a request. NOT a loan.
 *
 * There is deliberately no balance, no disbursement date and no ledger reference on this type: an
 * expression of interest is not an obligation, and a shape that could carry one would eventually be
 * given one.
 */
export interface FinancingOffer {
  id: string;
  request_id: string;
  financer_id: string;
  financer_name: string | null;
  product_id: string;
  product_name: string | null;
  amount_minor: string;
  asset: string;
  rate_bps: number;
  tenor_months: number;
  repayment_frequency: string;
  fees_minor: string;
  total_repayable_minor: string;
  /** The key facts a borrower must be shown before acceptance (FR-U-CRD-014). Frozen at offer time. */
  key_facts: {
    principal_minor: string;
    interest_minor: string;
    fees_minor: string;
    total_repayable_minor: string;
    cost_of_credit_minor: string;
    asset: string;
    rate_bps: number;
    tenor_months: number;
    repayment_frequency: string;
    fee_items: { code: string; label: string; amount_minor: string; when: string }[];
  };
  conditions: string[];
  state: 'submitted' | 'under_review' | 'accepted' | 'rejected' | 'expired' | 'cancelled';
  decline_reason: string | null;
  approved_at: string | null;
  created_at: string;
  expires_at: string;
}

export interface CreditLoan {
  id: string;
  request_id: string | null;
  request_reference: string | null;
  offer_id: string | null;
  borrower_id: string;
  borrower_name: string | null;
  lender_id: string;
  lender_name: string | null;
  product_id: string;
  product_name: string | null;
  principal_minor: string;
  outstanding_minor: string;
  asset: string;
  state: string;
  rate_bps: number | null;
  tenor_months: number | null;
  repayment_frequency: string | null;
  fees_minor: string;
  total_repayable_minor: string | null;
  principal_repaid_minor: string;
  interest_paid_minor: string;
  fees_paid_minor: string;
  repaid_pct: number;
  next_due_at: string | null;
  arrears_since: string | null;
  grace_expires_at: string | null;
  created_at: string;
  disbursed_at: string | null;
  closed_at: string | null;
}

export interface CreditLoanDetail extends CreditLoan {
  schedule: {
    n: number;
    due_at: string;
    principal_minor: string;
    interest_minor: string;
    fee_minor: string;
    total_minor: string;
    paid_minor: string;
    state: string;
  }[];
  repayments: {
    id: string;
    transaction_id: string;
    amount_minor: string;
    asset: string;
    applied_fees_minor: string;
    applied_interest_minor: string;
    applied_principal_minor: string;
    interest_rebate_minor: string;
    is_early_settlement: boolean;
    paid_at: string;
  }[];
  collateral: CollateralPosition[];
}

/**
 * How covered a loan is, and by how much it is short (FR-X-CRD-009, FR-U-CRD-013).
 *
 * `shortfall_minor` is the repayment that brings the loan back under the product CEILING, not merely
 * under the call trigger: curing to exactly the trigger line leaves the borrower one small market
 * move from being called again. The borrower and the financier are shown the SAME numbers, from the
 * same server function; a friendlier version for the borrower would tell them they were fine while
 * the lender's screen said otherwise, and only one of the two can act in time.
 */
export interface LoanCoverage {
  ltv_bps: number;
  eligible_minor: string;
  outstanding_minor: string;
  ceiling_bps: number;
  margin_call_bps: number;
  liquidation_bps: number;
  shortfall_minor: string;
}

/** A demand with a deadline. Not an enforcement: nothing happens to the asset before it expires. */
export interface MarginCall {
  id: string;
  loan_id: string;
  issued_at: string;
  ltv_at_issue_bps: number;
  shortfall_minor: string;
  shortfall_asset: string;
  cure_deadline: string;
  state: 'open' | 'cured' | 'expired' | 'withdrawn';
  resolved_at: string | null;
}

/** What a financer sees in the marketplace. The borrower view is deliberately THIN: see the service. */
export interface FinancingOpportunity {
  request_id: string;
  reference: string;
  product: { id: string; name: string | null; max_ltv_bps: number; rate_bps: number };
  amount_minor: string;
  asset: string;
  tenor_months: number;
  repayment_frequency: string;
  purpose: string;
  state: CreditRequestState;
  financing_deadline: string | null;
  submitted_at: string | null;
  live_offers: number;
  my_offers: number;
  collateral: CollateralPosition[];
  eligible_collateral_minor: string;
  max_financing_minor: string;
  ltv_bps: number;
  borrower: { tier: string; since: string | null; risk_state: string };
}

export interface FinancerOffer {
  id: string;
  request_id: string;
  request_reference: string;
  request_state: CreditRequestState;
  purpose: string;
  product_name: string | null;
  amount_minor: string;
  asset: string;
  rate_bps: number;
  tenor_months: number;
  total_repayable_minor: string;
  state: string;
  approved_at: string | null;
  loan_id: string | null;
  loan_state: string | null;
  /** interest_expressed | awaiting_approval | committed | disbursed | closed. Derived server-side. */
  stage: string;
  expires_at: string;
}

export interface FinancerPortfolio {
  loans: CreditLoan[];
  collateral: {
    position_id: string;
    loan_id: string;
    asset_name: string;
    pledged_bps: number;
    eligible_minor: string;
    state: string;
    loan_state: string;
    outstanding_minor: string;
  }[];
  recoveries: {
    enforcement_id: string;
    loan_id: string;
    stage: string;
    reason: string;
    outstanding_at_declaration_minor: string;
    realization_id: string | null;
    realization_state: string | null;
    proceeds_minor: string | null;
    declared_at: string;
  }[];
  summary: {
    active: number;
    outstanding_minor: string;
    disbursed_minor: string;
    repaid: number;
    defaulted: number;
    recovered: number;
    in_arrears: number;
  };
}

/**
 * A Trust Entity: the legal Special Purpose Trust that HOLDS a real-world asset, against which tokens
 * are issued. The bridge between the asset and the token (ADR 0005).
 */
export interface Trust {
  id: string;
  legal_name: string;
  kind: string;
  jurisdiction: string;
  registration_number: string;
  status: string;
  asset: string;
  asset_class: string;
  trustee: string;
  beneficiaries: number;
  holders: number;
  valuation: string;
  created_at: string;
  distributions: { kind: string; amount: string; at: string }[];
}

// ---------------------------------------------------------------------------
// Sponsor issuance (the economic-vs-legal split, ADR 0005 extended).
//
// The sponsor NEVER mints. They submit an asset; the VAST Holding Entity takes legal title; the Trust
// generates the capital structure and mints; the Trust allocates an initial issuance to the sponsor;
// and the sponsor then decides how much of THEIR allocation to bring to market, as offerings the Trust
// must approve before they publish. This mirrors regulated RWA issuance rather than "user tokenizes".
// ---------------------------------------------------------------------------

export type SponsorStage =
  'draft' | 'submitted' | 'diligence' | 'valuation' | 'formation' | 'allocation' | 'offering';

export interface SponsorReviewStep {
  key: string;
  label: string;
  /** Maps onto the shared status vocabulary: verified | in review | pending. */
  status: string;
}

export interface SponsorValuation {
  independent: string;
  approved: string;
  valuer: string;
  date: string;
}

/** The capital structure is GENERATED BY THE TRUST, not chosen by the sponsor. The sponsor approves it. */
export interface CapitalStructure {
  approvedValue: string;
  totalSupply: number;
  tokenPrice: string;
  sponsorAllocation: number;
  marketplaceAllocation: number;
  reserve: number;
  approvedBySponsor: boolean;
}

/** The token treasury: the canonical split of a trust's supply. Every movement flows through it. */
export interface Treasury {
  total: number;
  sponsorTreasury: number;
  marketplaceInventory: number;
  investorHoldings: number;
  reserved: number;
  burned: number;
}

export interface Offering {
  id: string;
  name: string;
  tokens: number;
  price: string;
  sold: number;
  opens: string;
  closes: string;
  /** draft | requested | approved | live | closed. The sponsor requests; the Trust approves. */
  status: string;
}

export interface SponsorAsset {
  id: string;
  name: string;
  type: string;
  category: string;
  location: string;
  estimatedValue: string;
  stage: SponsorStage;
  trust: {
    id: string | null;
    name: string | null;
    holdingEntity: string;
    trustNumber: string | null;
  };
  review: SponsorReviewStep[];
  valuation: SponsorValuation | null;
  capital: CapitalStructure | null;
  treasury: Treasury | null;
  offerings: Offering[];
  capitalRaised: string;
}

export interface SponsorSummary {
  assets: number;
  trusts: number;
  liveOfferings: number;
  capitalRaised: string;
  unsoldTokens: number;
}

export interface SponsorOffering extends Offering {
  asset: string;
  assetId: string;
}

export interface Distribution {
  id: string;
  asset: string;
  assetId: string;
  period: string;
  kind: string;
  gross: string;
  toInvestors: string;
  toSponsor: string;
  at: string;
  status: string;
}

export interface Distributions {
  paidToDate: string;
  nextRun: string;
  history: Distribution[];
}

// ---------------------------------------------------------------------------
// THE TOKENIZATION LIFECYCLE (migration 0055, svc-tokenization).
//
// These types are the WIRE: snake_case where the backend is snake_case, money as exact integer
// strings of minor units, token quantities as decimal strings. `call<T>` only casts, so a type that
// disagrees with the service is a screen that works under fixtures and breaks against the gateway -
// which is exactly how the `correlationId` mismatch shipped a broken link.
//
// The shape to hold on to: an APPLICATION is the lifecycle record, an ASSET is the real-world thing,
// a TOKEN is what gets issued against it, and a LISTING is a quantity of a token on the marketplace.
// Four entities related by id, none of them duplicating another's data.
// ---------------------------------------------------------------------------

export type TokenizationStatus =
  | 'draft'
  | 'submitted'
  | 'under_vasp_review'
  | 'information_requested'
  | 'under_trust_review'
  | 'approved_for_valuation'
  | 'valuation_in_progress'
  | 'valuation_submitted'
  | 'under_trust_reconciliation'
  | 'tokenization_offer_pending'
  | 'offer_pending_user_approval'
  | 'offer_pending_vasp_approval'
  | 'tokenization_authorized'
  | 'tokens_issued'
  | 'listed_on_secondary_market'
  | 'completed'
  | 'rejected'
  | 'withdrawn';

/** One row of the progress tracker. The BACKEND decides which stage a status falls in, not the UI. */
export interface LifecycleStage {
  label: string;
  state: 'done' | 'current' | 'pending';
}

export type FieldKind =
  | 'text'
  | 'long_text'
  | 'money'
  | 'number'
  | 'area'
  | 'rate'
  | 'years'
  | 'date'
  | 'select'
  | 'boolean';

/** A field the selected tokenization product asks for. The submission form is GENERATED from these. */
export interface ProductField {
  section: 'asset' | 'ownership' | 'tokenization';
  key: string;
  label: string;
  kind: FieldKind;
  unit: string | null;
  required: boolean;
  help: string | null;
  options: string[];
}

/** A document type the product requires, and how many of it. */
export interface ProductDocument {
  doc_type: string;
  required: boolean;
  min_count: number;
  note: string;
}

export interface TokenizationProduct {
  id: string;
  code: string;
  asset_class: string;
  name: string;
  summary: string;
  structure: string;
  classification: string;
}

export interface ProductConfig extends TokenizationProduct {
  fields: ProductField[];
  documents: ProductDocument[];
}

export interface UploadedDocument {
  document_id: string;
  doc_type: string;
  file_name: string | null;
  content_type: string | null;
  byte_size: number | null;
  /** SHA-256 of the bytes, computed by the SERVER. What makes the row evidence rather than a claim. */
  content_hash: string;
  status: 'submitted' | 'accepted' | 'rejected';
  uploaded_at: string;
  review_note: string | null;
}

/** What the product requires against what has been uploaded. THE definition of a complete submission. */
export interface ChecklistItem {
  doc_type: string;
  required: boolean;
  min_count: number;
  note: string;
  provided: number;
  rejected: number;
  satisfied: boolean;
}

export interface ValuationSummary {
  report_id: string;
  method: 'cost' | 'income' | 'market';
  basis: string;
  parameters: Record<string, unknown>;
  indicated_value: string | null;
  /** Minor units, exact integer string. */
  adopted_value: string;
  asset_code: string;
  variance_bps: number | null;
  justification: string | null;
  forced_sale_value: string | null;
  replacement_value: string | null;
  income_value: string | null;
  notes: string | null;
  risk_observations: { kind: string; severity: string; detail: string }[];
  valuer: string;
  valuer_licence: string | null;
  status: 'submitted' | 'accepted' | 'rejected' | 'revaluation_requested';
  confirmed: boolean;
  effective_from: string | null;
  submitted_at: string;
  reconciled_at: string | null;
  reconciliation_note: string | null;
  report_document_id: string | null;
}

/**
 * The tokenization offer, and its THREE approvals.
 *
 * `approvals` carries three timestamps rather than one boolean on purpose. An owner is entitled to
 * see that the trust has signed and the platform has not, because those are two different things
 * standing between them and their tokens.
 */
export interface TokenizationOffer {
  offer_id: string;
  status: 'pending_user' | 'pending_vasp' | 'authorized' | 'issued' | 'rejected' | 'superseded';
  token: {
    name: string;
    symbol: string;
    /** WHOLE tokens. */
    quantity: number;
    /** Minor units per whole token. */
    unit_value: string;
    asset_code: string;
    total_value: string;
    tokenized_bps: number;
    classification: string;
  };
  valuation: { effective_from: string | null; method: string; valuer: string };
  terms: Record<string, unknown>;
  approvals: { trust: string | null; user: string | null; platform: string | null };
  fully_approved: boolean;
  rejection_reason: string | null;
  rejected_by: string | null;
  token_id: string | null;
  created_at: string;
}

/** One entry in the audit trail: actor, role at the time, action, and the two statuses. */
export interface LifecycleEvent {
  from: TokenizationStatus;
  to: TokenizationStatus;
  actor: 'applicant' | 'vasp' | 'trust' | 'valuer' | 'system';
  actor_id: string | null;
  actor_role: string | null;
  action: string;
  note: string | null;
  at: string;
}

export interface TokenizationApplication {
  application_id: string;
  reference: string;
  status: TokenizationStatus;
  stages: LifecycleStage[];
  product: {
    code: string;
    name: string;
    structure: string;
    classification: string;
    asset_class: string;
  };
  asset: {
    asset_id: string;
    owner_id: string;
    ownership_reference: string;
    verification_status: string;
    legal_entity: { name: string; registration_number: string } | null;
  };
  details: Record<string, string>;
  proposal: Record<string, unknown>;
  information_requested: string | null;
  rejection_reason: string | null;
  assigned_valuer: string | null;
  assigned_at: string | null;
  created_at: string;
  submitted_at: string | null;
  updated_at: string;
  documents: UploadedDocument[];
  checklist: ChecklistItem[];
  valuation: ValuationSummary | null;
  offer: TokenizationOffer | null;
  token: {
    token_id: string;
    name: string | null;
    symbol: string | null;
    state: string;
    /** Whole tokens as a decimal string, eight places. Never a number. */
    total_supply: string;
    unit_value: string | null;
    quote_asset: string | null;
  } | null;
  history: LifecycleEvent[];
}

/** The dashboard row. Enough to say what it is and where it is, and no more. */
export interface ApplicationSummary {
  application_id: string;
  reference: string;
  status: TokenizationStatus;
  stages: LifecycleStage[];
  product: string;
  asset_class: string;
  name: string;
  ownership_reference: string;
  token_id: string | null;
  token_symbol: string | null;
  updated_at: string;
}

/**
 * A token the participant holds, with the asset behind it.
 *
 * `units` and `units_available` differ by what is listed. A holder who has listed 2,000 of 10,000
 * still HOLDS 10,000, and showing the available figure as the holding would tell them they had sold
 * something they have only offered.
 */
export interface TokenHolding {
  token_id: string;
  name: string | null;
  symbol: string | null;
  state: string;
  units: string;
  units_listed: string;
  units_available: string;
  fraction_bps: number;
  total_supply: string;
  unit_value: string | null;
  quote_asset: string | null;
  value: string | null;
  issued_at: string;
  underlying: {
    asset_id: string;
    asset_class: string;
    ownership_reference: string;
    description: string | null;
    location: string | null;
    held_by: string | null;
    product: string | null;
    application_id: string | null;
    reference: string | null;
    application_status: TokenizationStatus | null;
  };
}

/** A tokenized asset on the marketplace. The underlying is on every row, by join, never copied. */
export interface SecondaryListing {
  listing_id: string;
  token_id: string;
  token_name: string | null;
  symbol: string | null;
  units_listed: string;
  units_available: string;
  minimum: string;
  price: string;
  quote_asset: string;
  note: string | null;
  listed_at: string;
  expires_at: string | null;
  underlying: {
    asset_id: string;
    asset_class: string;
    description: string | null;
    location: string | null;
    ownership_reference: string;
    held_by: string | null;
    total_supply: string;
    issue_price: string | null;
    valuation: string | null;
    valuation_asset: string | null;
    valued_at: string | null;
  };
}

/** What the uploader accepts. SERVED rather than duplicated here, so the two cannot drift apart. */
export interface UploadPolicy {
  accept: string[];
  max_bytes: number;
  note: string;
}

/** The sponsor's tokenization design, submitted with the asset. Not a mint instruction: a proposal. */
export interface TokenizationProposal {
  preferredRaise: string;
  retainPct: number;
  offerPct: number;
  minInvestment: string;
  incomeSharing: string;
  voting: boolean;
  lockupMonths: number;
  exitStrategy: string;
}

// ---------------------------------------------------------------------------
// The converter (UF-04, FR-U-TXN-003/004).
//
// `live: false` means the number is INDICATIVE (the simulator, or a fallback when the live feed is
// unreachable). The UI must say so rather than dress an invented price up as a market quote.
// ---------------------------------------------------------------------------

export interface RatePair {
  asset: string;
  /** KES for one whole unit of `asset`, as a decimal string. KES itself is "1". */
  kes_per_unit: string;
}

export interface RateBoard {
  numeraire: string;
  source: string;
  as_of: string;
  live: boolean;
  fee_bps: number;
  rates: RatePair[];
}

export interface QuoteMoney {
  /** Exact minor units, as a string (an 18-decimal asset overflows a JS-safe integer). */
  amount: string;
  asset: string;
  /** The human decimal, e.g. "0.50000000". Prepend the asset code yourself. */
  display: string;
}

export interface Quote {
  from: string;
  to: string;
  rate: string;
  pay: QuoteMoney;
  gross: QuoteMoney;
  fee: QuoteMoney;
  fee_bps: number;
  receive: QuoteMoney;
  source: string;
  as_of: string;
  live: boolean;
}

export const api = {
  /**
   * Register. Returns a ONE-TIME enrolment token.
   *
   * That token is the only thing that can set this account's first PIN, and it is shown exactly once.
   * It exists because putting every endpoint behind a session made onboarding a closed loop: setting
   * a PIN needed a session, a session needed a sign-in, and a sign-in needed a PIN. Hold it, call
   * `enrolPin` with it, and it is consumed.
   */
  register: (phone: string, email: string, type: 'individual' | 'business') =>
    call<{ id: string; tier: string; status: string; enrolment_token: string }>(
      '/identity/register',
      { method: 'POST', body: JSON.stringify({ phone, email, type }) },
    ),

  /** Set the FIRST pin, using the token from registration, and sign in. */
  enrolPin: (enrolmentToken: string, pin: string) =>
    call<Session>('/identity/auth/enrol-pin', {
      method: 'POST',
      body: JSON.stringify({ enrolment_token: enrolmentToken, pin }),
    }),

  login: (phone: string, pin: string, device?: { fingerprint: string; name: string }) =>
    call<Session>('/identity/auth/login', {
      method: 'POST',
      body: JSON.stringify({
        phone,
        pin,
        device_fingerprint: device?.fingerprint ?? null,
        device_name: device?.name ?? null,
      }),
    }),

  /** The app calls this on every boot. The tier comes from the server, never from a cached value. */
  me: () => call<Me>('/identity/auth/me'),

  /** UF-09 §4.4: every device this account is signed in on. */
  sessions: () => call<ActiveSession[]>('/identity/auth/sessions'),

  revokeSession: (sessionId: string) =>
    call<{ revoked: boolean }>(`/identity/auth/sessions/${sessionId}/revoke`, { method: 'POST' }),

  logout: () => call<{ signed_out: boolean }>('/identity/auth/logout', { method: 'POST' }),

  /** UF-09 §5.2, "this was not me". One call, every session on the account gone. */
  logoutEverywhere: () =>
    call<{ sessions_ended: number }>('/identity/auth/logout-all', { method: 'POST' }),

  /** Change a PIN. The CURRENT one is required even though you are signed in: see ADR 0006. */
  changePin: (id: string, currentPin: string, newPin: string) =>
    call<{ pin_set: boolean; other_sessions_signed_out: number }>(`/identity/${id}/pin`, {
      method: 'POST',
      body: JSON.stringify({ current_pin: currentPin, new_pin: newPin }),
    }),

  consent: (id: string, consentType: string, version = 'v1.0') =>
    call<{ recorded: true }>(`/identity/${id}/consent`, {
      method: 'POST',
      body: JSON.stringify({ consentType, version }),
    }),

  /** The disclosures library (FR-U-DSP-004): every mandatory notice and this user's ack status. */
  disclosures: (id: string) => call<Disclosure[]>(`/identity/${id}/disclosures`),

  /**
   * Send a one-time code to the participant's phone or email (FR-U-ID-002).
   *
   * `dev_code` is returned ONLY under the simulator, so a sandbox tester can finish onboarding
   * without reading server logs. A real gateway leaves it null and the code arrives out of band.
   */
  requestContactCode: (id: string, channel: 'phone' | 'email') =>
    call<{ channel: string; expires_at: string; dev_code: string | null }>(
      `/identity/${id}/contact/${channel}/request`,
      { method: 'POST' },
    ),

  /** Submit a code. On success the channel is marked verified (FR-U-ID-002). */
  verifyContactCode: (id: string, channel: 'phone' | 'email', code: string) =>
    call<{ phone_verified: boolean; email_verified: boolean }>(
      `/identity/${id}/contact/${channel}/verify`,
      { method: 'POST', body: JSON.stringify({ code }) },
    ),

  contactStatus: (id: string) =>
    call<{ phone_verified: boolean; email_verified: boolean }>(`/identity/${id}/contact`),

  /**
   * Upload a KYC document. The BYTES go to the server, and THE SERVER HASHES THEM.
   *
   * The client is never asked for the content hash and would not be believed if it offered one. A
   * fingerprint chosen by the sender is a fingerprint for a file they may never have sent, and the
   * record would look exactly like evidence while attesting to nothing.
   */
  uploadDocument: (id: string, docType: DocType, file: File) => {
    const form = new FormData();
    form.append('doc_type', docType);
    form.append('file', file);

    return call<{ document_id: string; content_hash: string; doc_type: string }>(
      `/identity/${id}/documents`,
      { method: 'POST', body: form },
    );
  },

  /** Tier 1: the identity document and the liveness selfie. Screened, then scored, then whitelisted. */
  verifyTier1: (id: string, body: Tier1Request) =>
    call<{ tier: string; status: string; whitelisted: boolean; risk_band?: string }>(
      `/identity/${id}/verify/tier1`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /** Tier 2: proof of address and where the money comes from. Unlocks SENDING. */
  verifyTier2: (id: string, body: Tier2Request) =>
    call<{ tier: string; status: string; risk_band?: string }>(`/identity/${id}/verify/tier2`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  participant: (id: string) => call<Participant | null>(`/identity/${id}`),

  wallet: (id: string) => call<Wallet>(`/wallet/${id}`),

  /** UF_00 §6.3. Shown BEFORE composing, so a cap is never discovered at the till. */
  limits: (id: string) => call<Limits>(`/limits/${id}`),

  /** The converter's price board (FR-U-TXN-003). Display only; nothing here settles. */
  rates: () => call<RateBoard>('/rates'),

  /** What a conversion would look like, fees shown apart (FR-U-TXN-004). Reserves nothing. */
  quote: (from: string, to: string, amount: string) =>
    call<Quote>(`/rates/quote?from=${from}&to=${to}&amount=${encodeURIComponent(amount)}`),

  /** The tokenized-asset marketplace (super-app): listings a verified user can invest in. */
  marketplace: () => call<Marketplace>('/marketplace/listings'),
  listing: (id: string) => call<Listing>(`/marketplace/listings/${id}`),

  /** The investor portal: portfolio value, holdings, income. Same identity, "investor mode". */
  portfolio: () => call<Portfolio>('/portfolio'),

  /** Earnings & distributions across investments (dividends, rent, profit share, interest). */
  earnings: () => call<Earnings>('/earnings'),

  /** The secondary market: peer trading of already-issued tokens. */
  market: () => call<{ assets: MarketAsset[] }>('/market'),

  /**
   * The marketplace book. Willing buyer, willing seller.
   *
   * `side` is what YOU want to do: asking to buy returns offers to sell. Getting that backwards
   * shows an empty book to somebody looking at a busy market, which reads as "nobody is trading".
   */
  p2p: (asset = 'KES', side: 'buy' | 'sell' = 'buy') =>
    call<P2P>(`/p2p/offers?asset=${encodeURIComponent(asset)}&side=${side}`),

  /** Post an offer. The response states which release path the chosen methods earned. */
  /** The methods an offer may accept. Drives the seller's form, so it is never hardcoded. */
  p2pPaymentMethods: () => call<P2PPaymentMethods>('/p2p/payment-methods'),

  createP2POffer: (body: {
    side: 'buy' | 'sell';
    asset: string;
    quote_asset: string;
    price: string;
    total: string;
    minimum: string;
    maximum: string;
    methods: string[];
    terms?: string;
  }) => call<P2POffer>('/p2p/offers', { method: 'POST', body: JSON.stringify(body) }),

  /** The caller's own trades, open ones first. The way back to an asset locked in escrow. */
  p2pTrades: () => call<{ trades: P2PTrade[] }>('/p2p/trades'),

  // -------------------------------------------------------------------------------------------
  // USDT TILLS. A payment handle, mapped to a receiving wallet.
  //
  // NO BACKEND EXISTS FOR THESE YET, and these four lines are what a backend would have to serve.
  // They run against the fixture layer, which is why they are declared here beside the real
  // endpoints rather than faked inside a component: the day `svc-ledger-core` grows a `/tills`
  // router, the flag comes off and nothing above this line changes.
  //
  // The resolve call is deliberately a SERVER call rather than a lookup in a local list. A till a
  // participant has never seen has to resolve too, and only the service knows the mapping.
  // -------------------------------------------------------------------------------------------

  /** Every till this participant has, newest first. */
  tills: () => call<{ tills: Till[] }>('/tills'),

  /**
   * Which assets a till can be issued for, and which chains each one runs on.
   *
   * The asset list is the platform's own (`ASSET_SCALE` server side) minus fiat; the network list is
   * the custodian's. Both are SERVED so a screen cannot offer a chain the platform cannot receive on.
   */
  tillOptions: () => call<TillOptions>('/tills/options'),

  /**
   * Mint a till. IDEMPOTENT BY RULE, not by key: one active till per participant per asset AND
   * network, so a second press on the same pair returns the first one rather than issuing a second
   * handle for one destination. A different chain is a different destination and gets its own till.
   *
   * `network` is the TAG (`TRC20`), not the label, because that is what the till number carries.
   */
  createTill: (asset: string, network: string) =>
    call<Till>('/tills', { method: 'POST', body: JSON.stringify({ asset, network }) }),

  /** Who does this till pay? A 404 is a wrong till number, and the caller must say so plainly. */
  resolveTill: (tillNumber: string) =>
    call<TillResolution>(`/tills/${encodeURIComponent(tillNumber)}/resolve`),

  /** Retire a till. The handle stops resolving; nothing about past payments changes. */
  deactivateTill: (tillNumber: string) =>
    call<Till>(`/tills/${encodeURIComponent(tillNumber)}/deactivate`, { method: 'POST' }),

  /** Both settlement modes, priced. Never hides the instant option: it explains it. */
  settlementQuote: (asset: string) =>
    call<SettlementQuote>(`/merchant/settlements/quote?asset=${encodeURIComponent(asset)}`),

  /** Settle this merchant's takings. Moves value, so it carries an idempotency key. */
  settleMerchant: (asset: string, mode: 'standard' | 'instant') =>
    call<SettlementResult>('/merchant/settlements', {
      method: 'POST',
      body: JSON.stringify({ asset, mode }),
      headers: { 'Idempotency-Key': crypto.randomUUID() },
    }),

  /** What the platform has advanced this merchant and not yet recovered. */
  merchantAdvances: () => call<FloatAdvance[]>('/merchant/settlements/advances'),

  /** One trade. The trade room is built from this, so a refresh does not lose the deal. */
  p2pTrade: (tradeId: string) => call<P2PTrade>(`/p2p/trades/${tradeId}`),

  /** Start a trade. Locks the SELLER's asset; nothing is released and nobody is credited yet. */
  takeP2POffer: (offerId: string, amount: string, method: string) =>
    call<TakeOfferResult>(`/p2p/offers/${offerId}/take`, {
      method: 'POST',
      body: JSON.stringify({ amount, method }),
    }),

  /** The buyer says they have paid. A CLAIM: it releases nothing. */
  markP2PPaid: (tradeId: string) =>
    call<TradeStep>(`/p2p/trades/${tradeId}/paid`, { method: 'POST' }),

  /**
   * The seller confirms receipt. THIS releases the asset.
   *
   * Re-runs every compliance gate before posting, so it can be refused even though the trade was
   * allowed to start. When that happens the escrow stays held and the trade goes to dispute.
   */
  confirmP2PReceipt: (tradeId: string) =>
    call<TradeStep>(`/p2p/trades/${tradeId}/confirm`, { method: 'POST' }),

  /** Either party escalates. The escrow stays exactly where it is. */
  disputeP2PTrade: (tradeId: string) =>
    call<TradeStep>(`/p2p/trades/${tradeId}/dispute`, { method: 'POST' }),

  /**
   * Trade chat — the buyer↔seller conversation scoped to ONE trade. Text only, on-platform on
   * purpose: the counterparties never need to exchange a phone number or email to coordinate the
   * payment, so they never leave. Only a party to the trade may read or post (enforced server-side).
   */
  p2pMessages: (tradeId: string) =>
    call<{ trade_id: string; messages: P2PMessage[] }>(`/p2p/trades/${tradeId}/messages`),
  sendP2PMessage: (tradeId: string, body: string) =>
    call<{ message: P2PMessage }>(`/p2p/trades/${tradeId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),
  markP2PRead: (tradeId: string) =>
    call<{ ok: boolean }>(`/p2p/trades/${tradeId}/read`, { method: 'POST' }),
  p2pUnread: () => call<{ unread: number }>('/p2p/unread'),

  /**
   * Today's published board (FR-U-LQD-004).
   *
   * Read this to SHOW the rate, so it is something a participant checks rather than discovers at the
   * moment of paying. `date: null` means nothing is published; render that as "no rate today", never
   * as a live market number we have not committed to.
   */
  swapBoard: () => call<SwapBoard>('/transact/swap/board'),

  /**
   * Price an amount against today's board. Reserves nothing, settles nothing.
   *
   * Throws on refusal, carrying which of the eight it was. Read it with `refusalOf`.
   */
  swapQuote: (from: string, to: string, amount: string, side: 'buy' | 'sell' = 'buy') =>
    call<SwapQuote>('/transact/swap/quote', {
      method: 'POST',
      body: JSON.stringify({ from, to, amount, side }),
    }),

  /**
   * Execute the swap against the quote.
   *
   * The idempotency key is generated once by the caller and reused on any retry, for the same reason
   * `send` does it: two attempts with two keys are two different swaps.
   *
   * The quote is single-use server-side, so a double-submit is refused as `quote_expired` rather
   * than settling twice. That belt is deliberate and this brace is too.
   */
  swap: (quoteId: string, idempotencyKey: string) =>
    call<MovementResult & { quote: SwapQuote }>('/transact/swap', {
      method: 'POST',
      headers: { 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify({ quote_id: quoteId }),
    }),

  /** Investor analytics and tax statements. */
  analytics: () => call<Analytics>('/portfolio/analytics'),
  tax: () => call<Tax>('/portfolio/tax'),

  /** Connected bank accounts, M-Pesa, and external wallets. */
  connectedAccounts: () => call<ConnectedAccounts>('/accounts'),

  /** Credit lender portal (Phase 4): deploy capital against tokenized collateral. */
  lender: () => call<Lender>('/lender'),

  /** Merchant portal (Phase 5): accept payments, settle. */
  merchant: () => call<Merchant>('/merchant'),

  /** Business portal (Phase 5): the company and its people (Tier 3 KYB). */
  business: () => call<Business>('/business'),

  /**
   * Asset Finance Marketplace (borrower side). Credit organized around ASSETS: borrowing power, eligible
   * assets, loan products, a multi-DCP financing request, competing offers, loans, and collateral.
   */
  finance: {
    overview: () => call<FinanceOverview>('/finance/overview'),
    products: () => call<{ products: FinanceProduct[] }>('/finance/products'),
    assets: () => call<{ assets: EligibleAsset[] }>('/finance/assets'),
    offers: () => call<{ offers: FinanceOffer[] }>('/finance/offers'),
    offer: (id: string) => call<FinanceOffer>(`/finance/offers/${id}`),
    acceptOffer: (id: string) =>
      call<{ ok: boolean }>(`/finance/offers/${id}/accept`, { method: 'POST', body: '{}' }),
    declineOffer: (id: string) =>
      call<{ ok: boolean }>(`/finance/offers/${id}/decline`, { method: 'POST', body: '{}' }),
    loans: () => call<{ loans: FinanceLoan[] }>('/finance/loans'),
    loan: (id: string) => call<FinanceLoanDetail>(`/finance/loans/${id}`),
    collateral: () => call<{ collateral: CollateralRecord[] }>('/finance/collateral'),
    passport: (assetId: string) => call<AssetPassport>(`/finance/passport/${assetId}`),
    /** Submit a financing request to multiple eligible DCPs at once. */
    request: (body: {
      assetId: string;
      productId: string;
      amount: string;
      termMonths: number;
      purpose: string;
      dcps: string[];
    }) => call<{ id: string }>('/finance/requests', { method: 'POST', body: JSON.stringify(body) }),
    repay: (loanId: string, amount: string) =>
      call<{ ok: boolean }>(`/finance/loans/${loanId}/repay`, {
        method: 'POST',
        body: JSON.stringify({ amount }),
      }),
  },

  /**
   * THE TWO-SIDED CREDIT MARKETPLACE.
   *
   *   `credit.*`   the BORROWER: products, collateral, requests, offers received, loans, repayment
   *   `finance.*`  the FINANCER: opportunities, expressions of interest, approval, portfolio
   *
   * Both hit `svc-credit` through the gateway, EXCEPT `disburse` and `repay`, which are value
   * movements and therefore go to `svc-ledger-core` where the compliance pipeline lives. That split
   * is not an accident of routing: there is exactly one way to move value on this platform, and a
   * credit endpoint that moved money itself would be a second one.
   */
  creditMarket: {
    // MOCKS-OFF GUARD: `/credit/*` does not exist on the real backend. Rather than let the page hang
    // on a dead path or crash, the real branch resolves a schema-valid EMPTY value so the marketplace
    // renders an honest empty state. The MOCKS_ON branch is the full fixture and is untouched.
    products: (assetClass?: string) =>
      MOCKS_ON
        ? call<{ products: CreditProduct[] }>(
            `/credit/products${assetClass ? `?asset_class=${encodeURIComponent(assetClass)}` : ''}`,
          )
        : Promise.resolve({ products: [] as CreditProduct[] }),

    /** What can be pledged, what cannot, and why. `has_any` false is the route to tokenization. */
    eligibleCollateral: (productId?: string) =>
      MOCKS_ON
        ? call<{ collateral: EligibleCollateral[]; has_any: boolean; holds_tokens: boolean }>(
            `/credit/collateral/eligible${productId ? `?product_id=${encodeURIComponent(productId)}` : ''}`,
          )
        : Promise.resolve({
            collateral: [] as EligibleCollateral[],
            has_any: false,
            holds_tokens: false,
          }),

    /** Everything the borrower has encumbered, and against what (FR-U-CRD-006). */
    myCollateral: () =>
      MOCKS_ON
        ? call<{ collateral: CollateralPosition[] }>('/credit/collateral')
        : Promise.resolve({ collateral: [] as CollateralPosition[] }),

    requests: () =>
      MOCKS_ON
        ? call<{ requests: CreditRequest[] }>('/credit/requests')
        : Promise.resolve({ requests: [] as CreditRequest[] }),
    request: (id: string) => call<CreditRequestDetail>(`/credit/requests/${id}`),

    createRequest: (body: {
      product_id: string;
      amount: string;
      asset?: string;
      tenor_months: number;
      purpose: string;
      repayment_frequency?: string;
    }) => call<CreditRequest>('/credit/requests', { method: 'POST', body: JSON.stringify(body) }),

    /** Pledge units. `bps` is basis points of the token, never a token count. */
    pledge: (requestId: string, body: { token_id: string; bps: number; lien_priority?: number }) =>
      call<CollateralPosition>(`/credit/requests/${requestId}/collateral`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    unpledge: (requestId: string, positionId: string) =>
      call<{ ok: boolean }>(`/credit/requests/${requestId}/collateral/${positionId}`, {
        method: 'DELETE',
      }),

    /** Submit. The product's LTV ceiling is enforced server-side and in the database. */
    submitRequest: (id: string) =>
      call<CreditRequestDetail>(`/credit/requests/${id}/submit`, { method: 'POST', body: '{}' }),

    cancelRequest: (id: string) =>
      call<CreditRequest>(`/credit/requests/${id}/cancel`, { method: 'POST', body: '{}' }),

    /** Accept ONE offer. Every other live offer is declined in the same transaction, server-side. */
    acceptOffer: (requestId: string, offerId: string) =>
      call<CreditRequestDetail>(`/credit/requests/${requestId}/accept`, {
        method: 'POST',
        body: JSON.stringify({ offer_id: offerId }),
      }),

    loans: () =>
      MOCKS_ON
        ? call<{ loans: CreditLoan[] }>('/credit/loans')
        : Promise.resolve({ loans: [] as CreditLoan[] }),
    loan: (id: string) => call<CreditLoanDetail>(`/credit/loans/${id}`),

    /** Coverage and any margin calls. Same numbers the financier sees. */
    coverage: (loanId: string) =>
      call<{ coverage: LoanCoverage; margin_calls: MarginCall[] }>(
        `/credit/loans/${loanId}/coverage`,
      ),

    /**
     * Pledge more collateral against a LIVE loan, to cure a margin call.
     *
     * The first of the two remedies a margin call notice must offer (FR-U-CRD-013). The second is a
     * partial repayment, which is `repay` below and needs nothing new.
     */
    topUpCollateral: (loanId: string, body: { token_id: string; bps: number }) =>
      call<LoanCoverage>(`/credit/loans/${loanId}/collateral`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    /**
     * Repay. Goes to LEDGER-CORE, not the credit service: this moves money.
     *
     * The Idempotency-Key is required by the server, not optional politeness. Without it a dropped
     * response and a retry would pay twice.
     */
    repay: (loanId: string, amount: string, idempotencyKey: string, earlySettlement = false) =>
      call<{ movement: unknown; loan: CreditLoanDetail }>(`/credit/loans/${loanId}/repay`, {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKey },
        body: JSON.stringify({ amount, early_settlement: earlySettlement }),
      }),
  },

  /** The FINANCER side of the same marketplace. */
  financeMarket: {
    // MOCKS-OFF GUARD: `/finance/opportunities`, `/finance/offers` and `/finance/portfolio` (the
    // financer side of the marketplace) do not exist on the real backend. The real branch resolves a
    // schema-valid EMPTY value so these pages render an empty state instead of hanging or crashing.
    opportunities: (assetClass?: string) =>
      MOCKS_ON
        ? call<{ opportunities: FinancingOpportunity[] }>(
            `/finance/opportunities${assetClass ? `?asset_class=${encodeURIComponent(assetClass)}` : ''}`,
          )
        : Promise.resolve({ opportunities: [] as FinancingOpportunity[] }),
    opportunity: (requestId: string) =>
      call<CreditRequestDetail>(`/finance/opportunities/${requestId}`),

    /** Express interest, with terms. Creates an offer and nothing else: no loan, no commitment. */
    expressInterest: (
      requestId: string,
      body: {
        product_id: string;
        amount: string;
        asset?: string;
        rate_bps: number;
        tenor_months: number;
        repayment_frequency: string;
        conditions?: string[];
        expires_in_days?: number;
      },
    ) =>
      call<FinancingOffer>(`/finance/opportunities/${requestId}/offers`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    offers: () =>
      MOCKS_ON
        ? call<{ offers: FinancerOffer[] }>('/finance/offers')
        : Promise.resolve({ offers: [] as FinancerOffer[] }),

    /** The financer's FINAL approval. This is what creates the loan. */
    approve: (offerId: string, notes?: string) =>
      call<CreditLoanDetail>(`/finance/offers/${offerId}/approve`, {
        method: 'POST',
        body: JSON.stringify({ notes: notes ?? null }),
      }),
    decline: (offerId: string, reason: string) =>
      call<CreditRequestDetail>(`/finance/offers/${offerId}/decline`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),

    portfolio: () =>
      MOCKS_ON
        ? call<FinancerPortfolio>('/finance/portfolio')
        : Promise.resolve<FinancerPortfolio>({
            loans: [],
            collateral: [],
            recoveries: [],
            summary: {
              active: 0,
              outstanding_minor: '0',
              disbursed_minor: '0',
              repaid: 0,
              defaulted: 0,
              recovered: 0,
              in_arrears: 0,
            },
          }),

    /** Disburse an approved loan. LEDGER-CORE: this moves money. */
    disburse: (loanId: string, idempotencyKey: string) =>
      call<{ movement: unknown; loan: CreditLoanDetail }>(`/credit/loans/${loanId}/disburse`, {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKey },
        body: '{}',
      }),
  },

  /**
   * Sponsor issuance. The sponsor submits, tracks the Trust's review, approves the Trust-generated
   * capital structure, watches the treasury, and requests offerings. It never mints, prices, or sizes
   * the supply: those come back FROM the Trust.
   */
  sponsor: {
    summary: () => call<SponsorSummary>('/sponsor/summary'),
    offerings: () => call<{ offerings: SponsorOffering[] }>('/sponsor/offerings'),
    distributions: () => call<Distributions>('/sponsor/distributions'),
    assets: () => call<{ assets: SponsorAsset[] }>('/sponsor/assets'),
    asset: (id: string) => call<SponsorAsset>(`/sponsor/assets/${id}`),
    /** Submit an asset for issuance: info + media + documents + the tokenization proposal. */
    submit: (body: {
      type: string;
      name: string;
      details: Record<string, unknown>;
      proposal: TokenizationProposal;
    }) => call<{ id: string }>('/sponsor/assets', { method: 'POST', body: JSON.stringify(body) }),
    /** Approve the capital structure the Trust generated. The one decision that gates minting. */
    approveCapital: (id: string) =>
      call<{ ok: boolean }>(`/sponsor/assets/${id}/capital/approve`, {
        method: 'POST',
        body: '{}',
      }),
    /** Request an offering from the sponsor's own allocation. The Trust must approve before it lists. */
    requestOffering: (
      assetId: string,
      body: {
        name: string;
        tokens: number;
        price: string;
        opens: string;
        closes: string;
        story: string;
      },
    ) =>
      call<{ id: string }>(`/sponsor/assets/${assetId}/offerings`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  },

  /**
   * THE TOKENIZATION LIFECYCLE (migration 0055). The owner's half of it.
   *
   * Every route here is scoped to the caller server-side: the application must be theirs, the
   * documents must be on their application, the offer approval carries their own id and the database
   * refuses it otherwise. Nothing below is a permission check the UI performs.
   *
   * The other three parties (the platform, the trust, the valuer) have their own surfaces on their
   * own origins - `/admin/lifecycle`, `/trust/lifecycle`, `/valuer/lifecycle` - and none of them is
   * reachable from this app. That separation is the point: the officer surface must not sit one CORS
   * policy away from the customer app.
   */
  tokenization: {
    // MOCKS-OFF GUARD: the `/tokenize/lifecycle/*` lifecycle endpoints (except `/market`, which the
    // real backend now serves as `[]`) do not exist on the real backend. The real branch of each
    // collection read resolves an empty array so the lifecycle pages render an empty state rather than
    // hanging on a dead path. The MOCKS_ON fixtures are untouched.

    /** What can be tokenized, and under what structure. */
    products: () =>
      MOCKS_ON
        ? call<TokenizationProduct[]>('/tokenize/lifecycle/products')
        : Promise.resolve([] as TokenizationProduct[]),

    /** The fields and documents THIS product requires. The wizard is generated from this. */
    product: (code: string) => call<ProductConfig>(`/tokenize/lifecycle/products/${code}`),

    /** What the uploader accepts, so the file picker can say so before somebody picks a file. */
    uploadPolicy: () => call<UploadPolicy>('/tokenize/lifecycle/upload-policy'),

    applications: () =>
      MOCKS_ON
        ? call<ApplicationSummary[]>('/tokenize/lifecycle/applications')
        : Promise.resolve([] as ApplicationSummary[]),

    application: (id: string) =>
      call<TokenizationApplication>(`/tokenize/lifecycle/applications/${id}`),

    /** Start one. It lands in DRAFT: submitting is a separate, deliberate act. */
    start: (body: {
      product_code: string;
      ownership_reference: string;
      details: Record<string, string>;
      proposal?: Record<string, unknown>;
      owning_entity_id?: string | null;
    }) =>
      call<{ application_id: string; reference: string; asset_id: string; status: string }>(
        '/tokenize/lifecycle/applications',
        { method: 'POST', body: JSON.stringify(body) },
      ),

    update: (id: string, body: { details?: Record<string, string>; proposal?: object }) =>
      call<{ updated: boolean }>(`/tokenize/lifecycle/applications/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),

    /**
     * THE BYTES GO UP, and the SERVER hashes them.
     *
     * Not a hash this client computes. The whole value of content-hashing a title deed is that the
     * hash is a fact about the bytes; a fingerprint the uploader supplies is a fingerprint for a file
     * they may never have sent.
     *
     * `replaces` is what "remove and re-upload" means on an append-only evidence table: the old row is
     * superseded, never deleted.
     */
    upload: (applicationId: string, docType: string, file: File, replaces?: string) => {
      const form = new FormData();
      form.append('doc_type', docType);
      form.append('file', file);
      if (replaces) form.append('replaces', replaces);

      return call<{ document_id: string; content_hash: string }>(
        `/tokenize/lifecycle/applications/${applicationId}/documents`,
        { method: 'POST', body: form },
      );
    },

    removeDocument: (applicationId: string, documentId: string) =>
      call<{ withdrawn: boolean }>(
        `/tokenize/lifecycle/applications/${applicationId}/documents/${documentId}`,
        { method: 'DELETE' },
      ),

    /** Send it to the platform. Refused server-side while anything required is missing. */
    submit: (id: string) =>
      call<{ status: TokenizationStatus; label: string }>(
        `/tokenize/lifecycle/applications/${id}/submit`,
        { method: 'POST' },
      ),

    /**
     * The owner agrees to their asset being tokenized on these terms, or does not.
     *
     * THE CONSENT RECORD, and the reason the whole offer stage exists. Declining is not a rejection
     * of tokenization: the application goes back for the terms to be reworked.
     */
    decideOffer: (id: string, accept: boolean, reason?: string) =>
      call<{ status: TokenizationStatus }>(
        `/tokenize/lifecycle/applications/${id}/offer/decision`,
        { method: 'POST', body: JSON.stringify({ accept, reason: reason ?? null }) },
      ),

    /** The tokens this participant holds, each with the asset behind it. */
    portfolio: () =>
      MOCKS_ON
        ? call<TokenHolding[]>('/tokenize/lifecycle/portfolio')
        : Promise.resolve([] as TokenHolding[]),

    /**
     * List SOME of a holding. `tokens` is a decimal string of whole tokens, `unit_price` is minor
     * units per whole token: both strings, because they are a quantity of somebody's property and a
     * price, and a JSON number is an IEEE-754 double by the time it is parsed.
     *
     * Over-listing is refused by the database (race-critical guard 8), not by this call.
     */
    list: (
      tokenId: string,
      body: {
        tokens: string;
        unit_price: string;
        quote_asset?: string;
        minimum?: string | null;
        note?: string | null;
        expires_at?: string | null;
      },
    ) =>
      call<{
        listing_id: string;
        token_id: string;
        units: string;
        unit_price: string;
        quote_asset: string;
        remaining_available: string;
      }>(`/tokenize/lifecycle/tokens/${tokenId}/listings`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    myListings: () =>
      MOCKS_ON
        ? call<SecondaryListing[]>('/tokenize/lifecycle/listings')
        : Promise.resolve([] as SecondaryListing[]),

    cancelListing: (id: string) =>
      call<{ withdrawn: boolean }>(`/tokenize/lifecycle/listings/${id}`, { method: 'DELETE' }),

    /** The tokenized-asset side of the marketplace, with the underlying asset on every row. */
    market: () => call<SecondaryListing[]>('/tokenize/lifecycle/market'),
  },

  /** Trust entities (the legal SPV layer that holds a tokenized asset). */
  trusts: () => call<{ trusts: Trust[] }>('/trust'),
  trust: (id: string) => call<Trust>(`/trust/${id}`),
  registerTrust: (body: {
    kind: string;
    legal_name: string;
    jurisdiction: string;
    registration_number: string;
    trustee: string;
    asset: string;
  }) => call<{ id: string }>('/trust', { method: 'POST', body: JSON.stringify(body) }),

  // ---------------------------------------------------------------------------
  // NOTIFICATIONS (UF-08)
  //
  // No {participant_id} anywhere. There is nothing here a caller could legitimately want to read
  // about somebody else, and a path parameter would be an invitation to try.
  // ---------------------------------------------------------------------------

  notifications: () => call<Notification[]>('/notifications'),

  unreadCount: () => call<{ unread: number }>('/notifications/unread-count'),

  markRead: (id: string) =>
    call<{ read: boolean }>(`/notifications/${id}/read`, { method: 'POST' }),

  markAllRead: () => call<{ marked_read: number }>('/notifications/read-all', { method: 'POST' }),

  /** AGREED to. Not the same as read, and the difference is legal. */
  acknowledge: (id: string) =>
    call<{ acknowledged: boolean }>(`/notifications/${id}/acknowledge`, { method: 'POST' }),

  // ---------------------------------------------------------------------------
  // DISPUTES (UF-07). FR-U-DSP-001: the right this platform gives a person when
  // something goes wrong, and which until now it did not.
  // ---------------------------------------------------------------------------

  raiseDispute: (body: {
    category: DisputeCategory;
    subject: string;
    detail: string;
    /** The payment this is about, if they know it. Optional: they may not, and that is fine. */
    correlation_id?: string;
  }) =>
    call<{
      case_id: string;
      category: string;
      priority: string;
      status: string;
      we_will_answer_by: string;
    }>('/disputes', { method: 'POST', body: JSON.stringify(body) }),

  // ---------------------------------------------------------------------------
  // Assisted help (FR-U-DSP-006). See ADR 0007 for what it may and may not do.
  // ---------------------------------------------------------------------------

  assistantAvailability: () => call<AssistantAvailability>('/assistant/availability'),

  /**
   * Opt in. Recorded through the ORDINARY consent endpoint, because agreeing to have your words
   * sent to a processor outside Kenya is a Data Protection Act lawful basis, exactly like the
   * data-processing consent. It is not a special case and it should not have a special path.
   */
  acceptAssistedHelp: (participantId: string, version: string) =>
    call<{ recorded: boolean; version: string }>(`/identity/${participantId}/consent`, {
      method: 'POST',
      body: JSON.stringify({ consent_type: 'assisted_help', version }),
    }),

  askAssistant: (question: string, conversationId?: string) =>
    call<AssistantAnswer>('/assistant/ask', {
      method: 'POST',
      body: JSON.stringify({ question, conversation_id: conversationId ?? null }),
    }),

  disputes: () => call<Dispute[]>('/disputes'),

  dispute: (caseId: string) => call<Dispute>(`/disputes/${caseId}`),

  /** The BYTES go up and the SERVER hashes them. The uploader may be under investigation. */
  attachDisputeEvidence: (caseId: string, file: File, description?: string) => {
    const form = new FormData();
    form.append('file', file);
    if (description) form.append('description', description);

    return call<{ evidence_id: string; content_hash: string }>(`/disputes/${caseId}/evidence`, {
      method: 'POST',
      body: form,
    });
  },

  withdrawDispute: (caseId: string) =>
    call<{ withdrawn: boolean }>(`/disputes/${caseId}/withdraw`, { method: 'POST' }),

  // ---------------------------------------------------------------------------
  // TOKENIZATION (UF-05)
  // ---------------------------------------------------------------------------

  /**
   * Record the legal entity that HOLDS the property. The token is an interest in THIS.
   *
   * We do not form it. Forming a company happens with counsel, at the companies registry, in the real
   * world, and it takes real-world time. We record that it exists and that it holds the asset.
   */
  registerEntity: (body: {
    kind: 'spv' | 'series' | 'trust' | 'company';
    jurisdiction: string;
    legal_name: string;
    registration_number: string;
  }) =>
    call<{ entity_id: string }>('/tokenize/entities', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  submitAsset: (body: {
    asset_class: AssetClass;
    ownership_reference: string;
    details?: Record<string, unknown>;
    /** REQUIRED for land and real estate. The entity that holds the property. */
    owning_entity_id?: string;
  }) =>
    call<{ asset_id: string }>('/tokenize/assets', { method: 'POST', body: JSON.stringify(body) }),

  /** The title deed. The BYTES go up, and the SERVER hashes them. */
  attachAssetDocument: (assetId: string, docType: string, file: File) => {
    const form = new FormData();
    form.append('doc_type', docType);
    form.append('file', file);

    return call<{ document_id: string; content_hash: string }>(
      `/tokenize/assets/${assetId}/documents`,
      { method: 'POST', body: form },
    );
  },

  /** FR-U-TOK-002. Everything I have submitted, and where each one has got to. */
  myAssets: () => call<MyAsset[]>('/tokenize/my-assets'),

  assetStatus: (assetId: string) => call<AssetStatus>(`/tokenize/assets/${assetId}`),

  /** FR-U-TOK-003. The ownership certificate. */
  token: (tokenId: string) => call<TokenDetail>(`/tokenize/tokens/${tokenId}`),

  holdings: (participantId: string) => call<Holding[]>(`/tokenize/holdings/${participantId}`),

  locks: (id: string) => call<{ locks: Lock[] }>(`/wallet/${id}/locks`),

  history: (id: string) =>
    call<{
      transactions: {
        id: string;
        type: string;
        state: string;
        direction: 'in' | 'out';
        asset: string;
        amount: string;
        correlationId: string;
        at: string;
      }[];
    }>(`/wallet/${id}/history`),

  topUp: (id: string, amount: string, asset: string) =>
    call<Wallet>(`/wallet/${id}/dev-topup`, {
      method: 'POST',
      body: JSON.stringify({ amount, asset }),
    }),

  /** This participant's deposits and withdrawals. Scoped to the session server-side. */
  ramps: () => call<RampInstruction[]>('/wallet/ramps'),

  /**
   * Withdraw fiat to a bank account or an M-Pesa wallet (FR-U-WAL-006).
   *
   * The idempotency key is generated once by the CALLER and reused on retry, for the same reason it
   * is on `send`: a fresh key per attempt turns one payout into two.
   *
   * A REFUSAL THROWS HERE, and that is the opposite of `send`, which returns HTTP 200 carrying a
   * refusal body. The two paths genuinely differ: a blocked withdrawal raises `RampRefused` server
   * side, which is a `ValidationFailure` and therefore a 422. So callers must catch `LipaApiError`
   * and read `detail.message` for the disclosable reason, NOT inspect the resolved value.
   */
  withdraw: (
    amount: string,
    asset: string,
    rail: WithdrawalRail,
    destinationRef: string,
    idempotencyKey: string,
  ) =>
    call<WithdrawalReceipt>('/wallet/withdrawals', {
      method: 'POST',
      headers: { 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify({ amount, asset, rail, destination_ref: destinationRef }),
    }),

  /**
   * Sends value.
   *
   * The idempotency key is generated HERE, once, before the request goes out, and reused on any
   * retry. Generating a fresh one per attempt would defeat the entire mechanism: two attempts with
   * two keys are two different payments, and the participant would be debited twice.
   */
  send: (
    from: string,
    to: string,
    amount: string,
    asset: string,
    idempotencyKey: string,
    pin: string,
  ) =>
    call<MovementResult>('/transact/send', {
      method: 'POST',
      headers: { 'Idempotency-Key': idempotencyKey },
      // The PIN is a real step-up: the backend verifies it before moving money.
      body: JSON.stringify({ from, to, amount, asset, pin }),
    }),

  movement: (correlationId: string) =>
    call<{ correlationId: string; events: { type: string; at: string }[] } | null>(
      `/transact/${correlationId}`,
    ),

  regulatorDashboard: () =>
    call<{
      phase: string;
      killSwitchEngaged: boolean;
      exposure: { asset: string; total: string }[];
      transactions24h: Record<string, number>;
      integrity: { auditTrailIntact: boolean; ledgerBalanced: boolean };
    }>('/regulator/dashboard'),
};
